// Mezclador de movimientos. Trabaja con canales anatómicos (grados), sin three.js, así que mezclar es solo
// interpolar números:  un movimiento BASE (reposo / caminar) con crossfade + capas OVERLAY encima (saludar, hablar...).
import { CLIPS } from './clips.js';
import { neutral } from './anatomy.js';
import { clamp, lerp, smoothstep, rand01 } from './math.js';

/** Parpadeo realista: cada ~4 s (1.4–9 s), dura ~0.3 s (cierra rápido, abre lento) y a veces es doble. */
class Blinker {
  constructor() { this.t = 0; this.next = 1.8; this.b = -1; this.n = 0; }

  update(dt) {
    this.t += dt;
    if (this.b < 0 && this.t >= this.next) this.b = 0;
    let v = 0;
    if (this.b >= 0) {
      this.b += dt;
      const x = this.b / 0.3;
      v = x < 0.35 ? smoothstep(x / 0.35) : 1 - smoothstep((x - 0.35) / 0.65);
      if (this.b >= 0.3) {
        this.b = -1;
        this.n += 1;
        const dbl = rand01(this.n, 77) < 0.2;
        const gap = clamp(-Math.log(1 - rand01(this.n + 9, 5) * 0.98) * 4.2, 1.4, 9);
        this.next = this.t + (dbl ? 0.18 : gap);
      }
    }
    return v;
  }
}

export class Motion {
  constructor() {
    this.bases = []; // [{ name, inst, w, fade }]  (la última es la actual)
    this.overlays = []; // [{ id, name, def, inst, t, weight, fadeIn, fadeOut, stopT }]
    this.faceSet = {};
    this.lookT = { x: 0, y: 0 };
    this.lookV = { x: 0, y: 0 };
    this.blinker = new Blinker();
    this.nextId = 1;
    this.setBase('idle', {}, 0);
  }

  /** Cambia el movimiento base con crossfade (segundos). Si ya está activo, solo actualiza sus parámetros. */
  setBase(name, params = {}, fade = 0.45) {
    const def = CLIPS[name];
    if (!def || def.kind !== 'base') throw new Error(`"${name}" no es un movimiento base`);
    const cur = this.bases[this.bases.length - 1];
    if (cur && cur.name === name) { cur.inst.set?.(params); return cur.inst; }
    const inst = def.make(params);
    const entry = { name, inst, w: fade > 0 ? 0 : 1, fade };
    this.bases.push(entry);
    while (this.bases.length > 2) this.bases.shift();
    if (fade <= 0) this.bases = [entry];
    return inst;
  }

  /** Parámetros del base actual (por ejemplo { speed } al caminar). */
  setParams(params) { this.bases[this.bases.length - 1]?.inst.set?.(params); }

  /** Activa una capa encima del base. Devuelve un id. opts: { weight, fadeIn, fadeOut, duration, params } */
  play(name, opts = {}) {
    const def = CLIPS[name];
    if (!def || def.kind !== 'overlay') throw new Error(`"${name}" no es una capa`);
    // una capa del mismo tipo se reinicia en lugar de duplicarse
    this.overlays = this.overlays.filter((o) => o.name !== name);
    const o = {
      id: this.nextId++, name, def, inst: def.make(opts.params || {}), t: 0, stopT: -1,
      weight: opts.weight ?? 1, fadeIn: opts.fadeIn ?? 0.3, fadeOut: opts.fadeOut ?? 0.4,
      duration: opts.duration ?? def.duration ?? Infinity,
    };
    this.overlays.push(o);
    return o.id;
  }

  /** Detiene una capa con desvanecimiento (por nombre o id). */
  stop(nameOrId, fadeOut) {
    for (const o of this.overlays) {
      if ((o.name === nameOrId || o.id === nameOrId) && o.stopT < 0) { o.stopT = 0; if (fadeOut != null) o.fadeOut = fadeOut; }
    }
  }

  stopAll(fadeOut) { for (const o of this.overlays) this.stop(o.id, fadeOut); }

  isPlaying(name) { return this.overlays.some((o) => o.name === name && o.stopT < 0); }

  /** Mirar hacia (x, y) en [-1, 1] (+x derecha del avatar, +y arriba): mueve ojos, cabeza y cuello. */
  look(x = 0, y = 0) { this.lookT.x = clamp(x, -1, 1); this.lookT.y = clamp(y, -1, 1); }

  /** Cara manual: 'jaw' (audio), 'smile', 'brow', 'funnel' en 0..1. Se suma a lo que haga el movimiento. */
  setFace(name, value) { this.faceSet[name] = value; }

  /** Avanza dt segundos y devuelve el diccionario de canales anatómicos final. */
  update(dt) {
    dt = Math.min(dt, 0.05);
    // --- base(s) con crossfade
    const cur = this.bases[this.bases.length - 1];
    if (cur.w < 1) cur.w = Math.min(1, cur.w + dt / Math.max(cur.fade, 1e-3));
    let out = neutral();
    cur.inst.update(dt, out);
    if (this.bases.length > 1) {
      const prev = this.bases[0];
      const po = neutral();
      prev.inst.update(dt, po);
      const mixed = {};
      const w = smoothstep(cur.w);
      for (const k of Object.keys(out)) mixed[k] = lerp(po[k], out[k], w);
      out = mixed;
      if (cur.w >= 1) this.bases.shift();
    }

    // --- capas encima
    for (const o of this.overlays) {
      o.t += dt;
      if (o.stopT >= 0) o.stopT += dt;
      const env = smoothstep(o.t / Math.max(o.fadeIn, 1e-3));
      const tail = Math.min(
        o.duration === Infinity ? 1 : smoothstep((o.duration - o.t) / Math.max(o.fadeOut, 1e-3)),
        o.stopT >= 0 ? smoothstep(1 - o.stopT / Math.max(o.fadeOut, 1e-3)) : 1,
      );
      const w = env * tail * o.weight;
      o.done = o.t >= o.duration || (o.stopT >= 0 && o.stopT >= o.fadeOut);
      const tmp = {};
      o.inst.update(dt, tmp);
      for (const [k, val] of Object.entries(tmp)) {
        if (out[k] === undefined) continue;
        out[k] = o.def.additive ? out[k] + val * w : lerp(out[k], val, w);
      }
    }
    this.overlays = this.overlays.filter((o) => !o.done);

    // --- mirada
    const a = 1 - Math.exp(-dt * 6);
    this.lookV.x += (this.lookT.x - this.lookV.x) * a;
    this.lookV.y += (this.lookT.y - this.lookV.y) * a;
    const { x, y } = this.lookV;
    out['gaze.x'] += x;
    out['gaze.y'] += y;
    out['head.rot'] += 16 * x;
    out['neck.rot'] += 12 * x;
    out['head.flex'] -= 8 * y;
    out['neck.flex'] -= 6 * y;

    // --- cara manual y parpadeo
    for (const [k, v] of Object.entries(this.faceSet)) out[`face.${k}`] = (out[`face.${k}`] ?? 0) + v;
    out['face.blink'] = Math.max(out['face.blink'], this.blinker.update(dt));
    return out;
  }
}
